import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatError } from "../lib/api";
import { GoogleLogo, CheckCircle, XCircle, Sparkle } from "@phosphor-icons/react";
import Seo from "../components/Seo";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogle() {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [avail, setAvail] = useState(null); // null | true | false | 'invalid'
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);

  // Capture ?ref= and persist for OAuth round-trip
  useEffect(() => {
    const fromUrl = (searchParams.get("ref") || "").trim().toLowerCase();
    if (fromUrl) {
      sessionStorage.setItem("hv_ref", fromUrl);
      setRef(fromUrl);
      return;
    }
    const stored = sessionStorage.getItem("hv_ref");
    if (stored) setRef(stored);
  }, [searchParams]);

  useEffect(() => {
    const u = username.trim().toLowerCase();
    if (!u) { setAvail(null); return; }
    if (!USERNAME_RE.test(u)) { setAvail("invalid"); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/username/check?username=${encodeURIComponent(u)}`);
        setAvail(data.available);
      } catch { setAvail(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [username]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await register({ email, password, name, username: username.toLowerCase(), ref: ref || undefined });
      sessionStorage.removeItem("hv_ref");
      nav("/app/home");
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <><Seo title="Create your account" description="Join HigherVision — Hawaii's social network. Connect with builders, find jobs, host events, and grow your network across the Islands." />
    <div className="min-h-screen bg-background grid place-items-center px-6 py-10 relative">
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="relative w-full max-w-md card-sharp p-8 fade-in">
        <Link to="/" className="font-display text-xl tracking-tight block mb-6">
          <span className="text-neon-pink">HIGHER</span><span className="text-neon-cyan">VISION</span>
        </Link>
        <h1 className="font-display text-3xl">Join the network.</h1>
        <p className="text-sm text-muted-foreground mt-1">Create your HigherVision account.</p>
        {ref && (
          <div data-testid="register-ref-pill" className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest border border-[#F2DC8C]/40 text-neon-lime px-2 py-1">
            <Sparkle size={11} weight="fill"/> Invited by @{ref}
          </div>
        )}

        <div className="mt-6 grid grid-cols-1 gap-2">
          <button data-testid="google-register-btn" onClick={startGoogle} className="btn-ghost w-full justify-center">
            <GoogleLogo size={16} weight="bold" /> Continue with Google
          </button>
        </div>

        <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
          <div className="flex-1 h-px bg-white/10" /> or <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          <label className="block text-xs uppercase tracking-widest text-muted-foreground">Name</label>
          <input data-testid="register-name" required className="input-sharp" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />

          <label className="block text-xs uppercase tracking-widest text-muted-foreground">Username</label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">@</span>
            <input
              data-testid="register-username"
              required
              className="input-sharp pl-7"
              value={username}
              onChange={(e) => setUsername(e.target.value.replace(/[^a-zA-Z0-9_]/g, "").toLowerCase())}
              placeholder="your_handle"
              minLength={3}
              maxLength={20}
              autoCapitalize="off"
              autoCorrect="off"
            />
            {avail === true && (
              <span data-testid="username-available" className="absolute right-2 top-1/2 -translate-y-1/2 text-[#F2DC8C] flex items-center gap-1 text-[10px] uppercase tracking-widest"><CheckCircle size={14} weight="fill"/> Free</span>
            )}
            {avail === false && (
              <span data-testid="username-taken" className="absolute right-2 top-1/2 -translate-y-1/2 text-[#D4AF37] flex items-center gap-1 text-[10px] uppercase tracking-widest"><XCircle size={14} weight="fill"/> Taken</span>
            )}
            {avail === "invalid" && (
              <span data-testid="username-invalid" className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-[10px] uppercase tracking-widest">3-20 · a-z 0-9 _</span>
            )}
          </div>

          <label className="block text-xs uppercase tracking-widest text-muted-foreground">Email</label>
          <input data-testid="register-email" required type="email" className="input-sharp" value={email} onChange={(e) => setEmail(e.target.value)} />
          <label className="block text-xs uppercase tracking-widest text-muted-foreground">Password</label>
          <input data-testid="register-password" required type="password" className="input-sharp" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 chars" />
          {err && <div data-testid="register-error" className="text-sm text-[#D4AF37]">{err}</div>}
          <button
            data-testid="register-submit"
            disabled={busy || avail !== true}
            type="submit"
            className="btn-neon w-full justify-center mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? "Creating…" : "Create Account"}
          </button>
        </form>

        <div className="mt-6 text-sm text-muted-foreground">
          Already have one? <Link to="/login" className="text-neon-cyan" data-testid="register-to-login">Sign in</Link>
        </div>
      </div>
    </div>
    </>
  );
}
